import React from 'react'

import { Container, Typography } from '@material-ui/core'
import { IUserClaim } from 'src/models/user'
import ImageDisplay from 'src/shared/views/ImageDisplay'
import { momentFormat } from 'src/utils/dates'
import styled from 'styled-components'

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  text-align: top;
  margin-bottom: 0.5rem;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.h2`
  margin-top: 0;
  margin-right: 1em;
`

const Text = styled(Typography)`
  margin-top: 0.25rem;
`

const UserLink = styled.a`
  display: grid;
`

interface IClaimDetails {
  claim: IUserClaim
}

const ClaimDetails = ({ claim }: IClaimDetails) => {
  const paymentMethod = React.useMemo(() => {
    switch (claim.paymentMethod?.type) {
      case 'DirectDepositInfo': {
        return (
          <ContentContainer>
            <Header>Direct Deposit</Header>
            <Column>
              <Text>Institution - {claim.paymentMethod.institutionNumber}</Text>
              <Text>Transit - {claim.paymentMethod.transitNumber}</Text>
              <Text>Account - {claim.paymentMethod.accountNumber}</Text>
            </Column>
          </ContentContainer>
        )
      }
      case 'ETransferEmail': {
        return (
          <ContentContainer>
            <Header>E-Transfer</Header>
            <Column>
              <Text>
                Full name - {claim.lastName}, {claim.firstName}
              </Text>
              <Text>Email - {claim.paymentMethod.email}</Text>
            </Column>
          </ContentContainer>
        )
      }
      case 'DirectDepositAccountId': {
        return (
          <ContentContainer>
            <Header>Direct Deposit Id</Header>
            <Column>
              <i>
                Something probably went wrong, please report if this is seen
              </i>
              <Text>ID - {claim.paymentMethod.id}</Text>
            </Column>
          </ContentContainer>
        )
      }
      default: {
        return (
          <ContentContainer>
            <Header>Payment</Header>
            <Column>
              <i>Unable to find payment information</i>
            </Column>
          </ContentContainer>
        )
      }
    }
  }, [claim.firstName, claim.lastName, claim.paymentMethod])

  return (
    <Container>
      <ContentContainer>
        <Header>Status</Header>
        <Text>{claim.status}</Text>
      </ContentContainer>
      <ContentContainer>
        <Header>Notes</Header>
        <Text>{claim.notes}</Text>
      </ContentContainer>
      <ContentContainer>
        <Header>Amount</Header>
        <Text>
          {claim.amount.amount} {claim.amount.currencyCode}
        </Text>
      </ContentContainer>
      {claim.programName ? (
        <ContentContainer>
          <Header>Program</Header>
          <Text>{claim.programName}</Text>
        </ContentContainer>
      ) : null}
      {claim.certificate &&
        claim.certificate.certificateNumber &&
        claim.certificate.planNumber &&
        claim.certificate.policyNumber && (
          <ContentContainer>
            <Header>Certificate</Header>
            <Column>
              <Text>
                Certificate Number - {claim.certificate.certificateNumber}
              </Text>
              <Text>Policy - {claim.certificate.policyNumber}</Text>
              <Text>Plan - {claim.certificate.planNumber}</Text>
            </Column>
          </ContentContainer>
        )}
      {paymentMethod}
      {claim.attestationContactEmail && (
        <ContentContainer>
          <Header>Contact Email</Header>
          <Text>{claim.attestationContactEmail}</Text>
        </ContentContainer>
      )}
      <ContentContainer>
        <Header>Created At</Header>
        <Text>
          {momentFormat(new Date(claim.createdAt), 'MM-D-YYYY, h:mm a')}
        </Text>
      </ContentContainer>
      {claim.usersUsingSameEmail && claim.usersUsingSameEmail.length > 0 && (
        <ContentContainer>
          <Header>Users with the same E-transfer Email</Header>
          <Column>
            {claim.usersUsingSameEmail.map((user, index) => (
              <UserLink
                href={`/users/${user.userId}`}
                target="_blank"
                key={index}
              >
                <Text>
                  {user.firstName} {user.lastName}
                </Text>
              </UserLink>
            ))}
          </Column>
        </ContentContainer>
      )}
      <ContentContainer>
        <Header>Image{claim.images.length > 1 ? 's' : ''}</Header>
        {claim.images.map((key, index) => (
          <ImageDisplay imageKey={key} key={index} />
        ))}
      </ContentContainer>
    </Container>
  )
}

export default ClaimDetails
